import { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { ModalProps, ModalSize } from "./types";
import { cn } from "@/utils/cn";

const sizeStyles: Record<ModalSize, string> = {
  sm:   "max-w-sm",
  md:   "max-w-lg",
  lg:   "max-w-3xl",
  xl:   "w-full max-w-[min(96vw,1120px)]",
  full: "w-full max-w-[min(95vw,1600px)] h-[min(94dvh,calc(100dvh-2rem))] max-h-[min(94dvh,calc(100dvh-2rem))]",
};

export function Modal({
  open,
  onClose,
  title,
  children,
  size = "md",
  hideCloseButton = false,
  footer,
  closeOnBackdropClick = false,
  closeOnEscape = false,
  elevated = false,
}: ModalProps) {
  useEffect(() => {
    if (!closeOnEscape) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onClose, closeOnEscape]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className={cn(
        "fixed inset-0 flex min-h-full justify-center overflow-y-auto bg-black/50 px-3 py-6 backdrop-blur-sm animate-in fade-in duration-150 sm:px-4",
        elevated ? "z-[80]" : "z-50",
      )}
      onMouseDown={
        closeOnBackdropClick
          ? (e) => {
              if (e.target === e.currentTarget) onClose();
            }
          : (e) => {
              // Impede que cliques no fundo escuro propaguem ou acionem fechamento indireto.
              if (e.target === e.currentTarget) e.preventDefault();
            }
      }
      aria-modal="true"
      role="dialog"
      aria-label={typeof title === "string" ? title : "modal"}
    >
      <div
        className={cn(
          "my-auto w-full bg-white rounded-2xl shadow-modal flex flex-col",
          sizeStyles[size],
          size === "full" ? "overflow-hidden" : "max-h-[90vh]",
        )}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        {(title || !hideCloseButton) && (
          <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100 flex-shrink-0">
            {title && (
              <div className="flex-1 min-w-0">
                {typeof title === "string" ? (
                  <h2 className="text-base font-semibold text-slate-800 leading-tight truncate">{title}</h2>
                ) : (
                  title
                )}
              </div>
            )}
            {!hideCloseButton && (
              <button
                onClick={onClose}
                className="flex-shrink-0 p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                aria-label="Fechar"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        )}

        <div
          className={cn(
            "flex-1 min-h-0 min-w-0 w-full p-5 sm:p-6",
            size === "full" || size === "xl"
              ? "overflow-y-auto overflow-x-hidden"
              : "overflow-auto",
          )}
        >
          {children}
        </div>

        {footer && (
          <div className="px-6 py-4 border-t border-slate-100 flex-shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
